/**
 * contactTrigger.js
 *
 * ES6 Module entry point for initializing the ContactTrigger system.
 *
 * This module delegates instantiation to `ContactTriggerFactory`, providing either a
 * fully-functional browser implementation or a safe fallback stub for SSR/invalid states.
 *
 * Features:
 * - Uses ES Module imports/exports for clean syntax.
 * - SSR-compatible: no top-level references to window/document in stub.
 * - Graceful fallback on factory initialization failure.
 * - Exposes singleton ContactTrigger instance as default export.
 * - Exports `factory` helpers for diagnostics: initialize, getDebugInfo, getEnvironmentInfo, validateImplementations.
 *
 * Limitations:
 * - Relies on correct setup of `ContactTriggerFactory` under `./core/ContactTriggerFactory.js`.
 * - Stub class provides no-op implementations; core logic resides in factory-created class.
 */

/**
 * contactTrigger.js
 *
 * ES6 Module entry point for initializing the ContactTrigger system.
 *
 * This module delegates instantiation to `ContactTriggerFactory`, providing either a
 * fully-functional browser implementation or a safe fallback stub for SSR/invalid states.
 *
 * FIXED: Now properly accepts and forwards namespace parameter to factory-created instances
 */

import ContactTriggerFactory from './core/ContactTriggerFactory.js';

// Define the fallback error class stub
class ContactTriggerError {
  constructor() {
    console.error('❌ ContactTrigger is not available due to initialization errors');
  }

  init() { return this; }
  destroy() {}
  triggerContact() {}
  getDebugInfo() {
    const factoryInit = ContactTriggerFactory.initialize();
    return { error: 'Factory initialization failed', issues: factoryInit.issues };
  }

  static getGlobalDebugInfo() {
    return { error: 'Factory initialization failed' };
  }

  static forceCleanup() {}
}

// ContactTrigger class that properly forwards namespace to factory
class ContactTrigger {
  constructor(namespace) {
    // Initialize the factory and capture validation results
    const factoryInit = ContactTriggerFactory.initialize();
    
    if (factoryInit.success) {
      // Create the appropriate implementation and forward namespace
      const ContactTriggerImpl = ContactTriggerFactory.create();
      
      // FIXED: Properly instantiate with namespace parameter
      const instance = new ContactTriggerImpl(namespace);
      
      // Copy all methods and properties from the factory-created instance
      Object.setPrototypeOf(this, Object.getPrototypeOf(instance));
      Object.assign(this, instance);
      
      return this;
    } else {
      // Return error stub if factory initialization failed
      return new ContactTriggerError();
    }
  }

  // Static methods remain the same for backward compatibility
  static getGlobalDebugInfo() {
    try {
      const ContactTriggerImpl = ContactTriggerFactory.create();
      return ContactTriggerImpl.getGlobalDebugInfo ? ContactTriggerImpl.getGlobalDebugInfo() : {};
    } catch (error) {
      return { error: 'Failed to get global debug info', message: error.message };
    }
  }

  static forceCleanup() {
    try {
      const ContactTriggerImpl = ContactTriggerFactory.create();
      if (ContactTriggerImpl.forceCleanup) {
        ContactTriggerImpl.forceCleanup();
      }
    } catch (error) {
      console.error('Failed to force cleanup:', error);
    }
  }
}

// Attach diagnostic helpers to the main class
ContactTrigger.factory = {
  initialize: ContactTriggerFactory.initialize.bind(ContactTriggerFactory),
  getDebugInfo: ContactTriggerFactory.getDebugInfo.bind(ContactTriggerFactory),
  getEnvironmentInfo: ContactTriggerFactory.getEnvironmentInfo.bind(ContactTriggerFactory),
  validateImplementations: ContactTriggerFactory.validateImplementations.bind(ContactTriggerFactory)
};

export default ContactTrigger;
export { ContactTrigger as factory };